/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import {
    setActionItemsCount,
    setActionStripAvailableActionItems,
} from '../../../store/videoplayerui/infobanner/infobanner.reducers';
import { RootState } from '../../../store/root.reducers';
import { ActionsStripProps } from './ActionsStripProps';
import ActionItem from './ActionItem/ActionItem';
import { updateActionStripReferenceAction } from '../../../store/videoplayerui/infobanner/infobanner.actions';
import { Mousetrap } from '../../common';
import { InteractiveComponents } from '../../../core/enums/InteractiveComponents';
import { LiveTvActionStripInteractiveLayer } from '../InteractiveLayer';

import './ActionsStrip.scss';

const ActionsStrip: FunctionComponent<ActionsStripProps> = (props): JSX.Element => {
    const { actionItems, cursorState } = props;

    const actionsItemLength = useSelector((state: RootState) => state.infoBanner.actionItemsCount);

    const actionsStripHighlightIndex = useSelector(
        (state: RootState) => state.infoBanner.actionsStripHighlightIndex,
    );
    const actionsStripHighlightBorderActive = useSelector(
        (state: RootState) => state.infoBanner.actionsStripHighlightBorderActive,
    );

    const activeComponent = useSelector(
        (state: RootState) => state.interactiveLayer.activeComponent,
    );

    const actionStripRef = useRef<HTMLDivElement>(null);

    const availableActionItems: Array<any> = [];
    const dispatch = useDispatch();

    let actionItemsLength;

    const setActionItemsLength = () => {
        actionItemsLength = 0;
        for (let i = 0; i < actionItems.length; i += 1) {
            if (actionItems[i].visible) actionItemsLength += 1;
        }
    };

    const setAvailableActionItems = () => {
        for (let i = 0; i < actionItems.length; i += 1) {
            if (actionItems[i].visible) availableActionItems.push(actionItems[i]);
        }
    };

    useEffect(() => {
        setAvailableActionItems();
        dispatch(setActionStripAvailableActionItems(availableActionItems));
    }, [actionsStripHighlightIndex]);

    useEffect(() => {
        setActionItemsLength();
        dispatch(setActionItemsCount(actionItemsLength));
        return () => {
            dispatch(setActionItemsCount(0));
        };
    }, [actionItems]);

    const actionItemsDiv = actionItems
        .filter((actionItem) => actionItem.visible)
        .map((actionItem, index) => {
            return index === actionsStripHighlightIndex ? (
                <Mousetrap value={actionsStripHighlightIndex}>
                    <ActionItem
                        highlighted={index === actionsStripHighlightIndex}
                        name={actionItem.name}
                        key={index}
                        data-testid="action-strip-action-items"
                    />
                </Mousetrap>
            ) : (
                <ActionItem
                    highlighted={index === actionsStripHighlightIndex}
                    name={actionItem.name}
                    key={index}
                    data-testid="action-strip-action-items"
                />
            );
        });

    if (
        actionItems[0].name === Locale.getString(LocaleKeys.LIVE_TV_ACTION_STRIP_RESUME) &&
        actionItems[0].visible &&
        actionsStripHighlightIndex === 1
    ) {
        actionItemsDiv[0] = <div className="left-most-action-item-resume"></div>;
    }

    useEffect(() => {
        dispatch(updateActionStripReferenceAction(actionStripRef));
    }, [actionStripRef]);

    return (
        <React.Fragment>
            {cursorState ? <LiveTvActionStripInteractiveLayer /> : null}
            <div className="actions-strip-container">
                <div
                    className={`action-strip-highlight ${
                        !cursorState || actionsStripHighlightBorderActive
                            ? 'action-strip-highlight-border'
                            : ''
                    }`}
                    data-testid="action-strip-highlight"
                ></div>

                <div
                    className={`actions-strip ${
                        cursorState &&
                        activeComponent === InteractiveComponents.EAST &&
                        actionsItemLength > 1 &&
                        actionsItemLength !== actionsStripHighlightIndex + 1
                            ? 'actions-strip-right-highlight'
                            : ''
                    }
                            ${
                                cursorState &&
                                activeComponent === InteractiveComponents.WEST &&
                                actionsStripHighlightIndex
                                    ? 'actions-strip-left-highlight'
                                    : ''
                            }`}
                    data-testid="action-strip"
                >
                    <div className="action-items-container" ref={actionStripRef}>
                        {actionItemsDiv}
                    </div>
                </div>
                <div className="actions-strip-message-container">
                    <div className="actions-strip-message" data-testid="action-strip-message">
                        {
                            actionItems.filter((actionItem) => actionItem.visible)[
                                actionsStripHighlightIndex
                            ]?.message
                        }
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

ActionsStrip.propTypes = {
    actionItems: PropTypes.array.isRequired,
    cursorState: PropTypes.bool.isRequired,
};

export default ActionsStrip;
